import { WSClientEvents } from '@cloudike/web_core'
import SockJS from 'sockjs-client'
import { USER_DATA_KEY_IN_STORAGE, USER_TOKEN_KEY_IN_STORAGE } from 'shared/constants/user'
import { getCurrentAppConfig } from 'shared/config/appConfig'

import { WebSocketService } from './webSocketService'

interface WebSocketInstance {
  sock: SockJS | null
  ws: WebSocketService | null
  reconnectTries: number
  isConnecting: boolean
  listeners: Array<{ eventName: WSClientEvents, listener: (event: any) => void }>
  includeFamilyUserId: boolean
  isShared: boolean
  sharedId?: string
}

interface InitWsOptions {
  includeFamilyUserId?: boolean
  isShared?: boolean
  sharedId?: string
}

const wsInstances: Map<string, WebSocketInstance> = new Map()

const wait = (ms: number) => new Promise((res) => setTimeout(res, ms))

const getInstanceKey = (includeFamilyUserId: boolean, isShared: boolean, sharedId?: string): string => {
  if (isShared) {
    return `shared_${sharedId || 'default'}`
  }
  return includeFamilyUserId ? 'family' : 'default'
}

const getOrCreateInstance = (includeFamilyUserId: boolean, isShared: boolean, sharedId?: string): WebSocketInstance => {
  const key = getInstanceKey(includeFamilyUserId, isShared, sharedId)
  
  if (!wsInstances.has(key)) {
    wsInstances.set(key, {
      sock: null,
      ws: null,
      reconnectTries: 0,
      isConnecting: false,
      listeners: [],
      includeFamilyUserId,
      isShared,
      sharedId
    })
  }
  
  return wsInstances.get(key)!
}

const removeAllListeners = (instance: WebSocketInstance) => {
  if (instance.sock) {
    instance.listeners.forEach(({ eventName, listener }) => {
      instance.sock!.removeEventListener(eventName, listener)
    })
  }
}

const addAllListeners = (instance: WebSocketInstance, sockInstance: SockJS) => {
  instance.listeners.forEach(({ eventName, listener }) => {
    sockInstance.addEventListener(eventName, listener)
  })
}

const createClient = (instance: WebSocketInstance): SockJS => {
  if (instance.isConnecting) {
    return instance.sock as SockJS
  }
  
  instance.isConnecting = true
  
  if (instance.sock) {
    removeAllListeners(instance)
    instance.sock.close()
  }

  const wsUrl = instance.isShared 
    ? getCurrentAppConfig().sockjs_for_shares 
    : getCurrentAppConfig().sockjs

  instance.sock = new SockJS(wsUrl)

  instance.sock.onopen = function() {
    instance.isConnecting = false
    instance.reconnectTries = 0
    
    const token = localStorage.getItem(USER_TOKEN_KEY_IN_STORAGE)
    const familyUserId = instance.includeFamilyUserId 
      ? JSON.parse(localStorage.getItem(USER_DATA_KEY_IN_STORAGE) || '{}').family_user_id 
      : undefined

    const data: { token?: string, event_groups?: string[], jwt_token?: string, timestamp: number, user_id?: number, shared_id?: string } = {
      ...(!instance.isShared ? { event_groups: ["fs", "fs2", "photos", "family", "news", "comments"] } : {}),
      timestamp: new Date().getTime(),
      ...(instance.isShared && instance.sharedId ? { share_id: instance.sharedId } : {})
    }

    if (token) {
      data.token = token
    }

    if (familyUserId) {
      data.user_id = familyUserId
    }

    if (instance.isShared && instance.sharedId) {
      data.shared_id = instance.sharedId
    }

    instance.sock?.send(JSON.stringify(data))
  }

  instance.sock.onclose = async function(event: CloseEvent) {
    instance.isConnecting = false
    
    removeAllListeners(instance)

    if (event.wasClean) {
      console.info(
        `Connection closed cleanly code = ${event.code} reason = ${event.reason}`
      )
    } else {
      console.info(
        `Connection interrupted code = ${event.code} reason = ${event.reason}`
      )
      
      await wait(Math.min(2 ** ++instance.reconnectTries * 1000, 30000)) // Максимум 30 секунд

      if (instance.reconnectTries <= 5) {
        const newSock = createClient(instance)
        addAllListeners(instance, newSock)
      }
    }
  }

  instance.sock.onerror = function(error) {
    instance.isConnecting = false
    console.error('WebSocket error:', error)
  }

  return instance.sock
}

export const initWs = (options: InitWsOptions = {}): WebSocketService => {
  const { 
    includeFamilyUserId = false, 
    isShared = false, 
    sharedId 
  } = options

  const instance = getOrCreateInstance(includeFamilyUserId, isShared, sharedId)
  
  if (instance.ws && instance.sock && instance.sock.readyState === SockJS.OPEN) {
    return instance.ws
  }

  const sockInstance = createClient(instance)

  const wrappedSock = {
    client: sockInstance,
    addListener: (eventName: WSClientEvents, listener: (event: any) => void) => {
      if (eventName === WSClientEvents.MESSAGE) {
        instance.listeners.push({
          eventName,
          listener
        })

        sockInstance.addEventListener(WSClientEvents.MESSAGE, listener)
      }
    }
  }

  instance.ws = new WebSocketService(wrappedSock, true, instance.isShared)
  return instance.ws
}

export const initSharedWs = (sharedId: string): WebSocketService => {
  return initWs({ isShared: true, sharedId })
}
